import React from "react";
import * as styles from "@styles/DocumentSelection.css";
import "@styles/util/DocumentSelection.global.css";
import { LayoutGrid, List } from "lucide-react";
import type { IOts } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";
import EmptyView from "@view/EmptyView";

export type DocumentSelectionCreatePhase = "hidden" | "visible";
export type DocumentSelectionListPhase = "empty" | "list";
export type DocumentSelectionModalPhase = "hidden" | "visible";
export type DocumentSelectionTogglePhase = "grid" | "list";

export type DocumentSelectionPanelProps = {
  createButtonPhase: DocumentSelectionCreatePhase;
  viewTogglePhase: DocumentSelectionTogglePhase;
  onCreateDocument: IOts<Ok>;
  onToggleView: IOts<Ok>;
  runIO: (effect: IOts<Ok>) => Ok;
  listPhase: DocumentSelectionListPhase;
  listView: React.ReactElement;
  emptyView: React.ReactElement;
  deleteModalPhase: DocumentSelectionModalPhase;
  deleteModalView: React.ReactElement;
};

const DocumentSelectionPanel: React.FC<DocumentSelectionPanelProps> = ({
  createButtonPhase,
  viewTogglePhase,
  onCreateDocument,
  onToggleView,
  runIO,
  listPhase,
  listView,
  emptyView,
  deleteModalPhase,
  deleteModalView,
}) => {
  const createButtonView = {
    hidden: <EmptyView />,
    visible: (
      <button className={styles.createButton} onClick={() => runIO(onCreateDocument)}>
        Create Document +
      </button>
    ),
  } as const satisfies Record<DocumentSelectionCreatePhase, React.ReactElement>;

  const toggleIcon = {
    grid: <LayoutGrid size={16} className={styles.icon} />,
    list: <List size={16} className={styles.icon} />,
  } as const satisfies Record<DocumentSelectionTogglePhase, React.ReactElement>;

  const listRender = {
    empty: emptyView,
    list: listView,
  } as const satisfies Record<DocumentSelectionListPhase, React.ReactElement>;

  const deleteModalRender = {
    hidden: <EmptyView />,
    visible: deleteModalView,
  } as const satisfies Record<DocumentSelectionModalPhase, React.ReactElement>;

  return (
    <div className={styles.container}>
      <div className={styles.textDropdown}>
        <p className={styles.text}>Document Studio</p>
        <div className={styles.headerActions}>
          {createButtonView[createButtonPhase]}
          <button
            className={styles.iconButton}
            onClick={() => runIO(onToggleView)}
            aria-label="Toggle view"
          >
            {toggleIcon[viewTogglePhase]}
          </button>
        </div>
      </div>

      {listRender[listPhase]}
      {deleteModalRender[deleteModalPhase]}
    </div>
  );
};

export default DocumentSelectionPanel;
